/* ============================================================
   WARMBELT — CINEMATIC HOME · interactions
   Vanilla JS, zero deps. GPU-transform only. Lazy below-fold.
   Respects prefers-reduced-motion. No layout thrash.

   v1.1 — IDLE PASS
   Every scroll-driven and ambient effect used to run on a
   permanent requestAnimationFrame loop: the parallax pass read
   getBoundingClientRect() on every tagged element 60x a second
   forever, the ambient particle canvas repainted forever, and
   the 360 viewer's auto-rotate ran a 60ms setInterval forever —
   all of it whether the page had moved, whether the element was
   on screen, or whether the tab was even visible. On a laptop
   that pins a core and drains battery while the visitor reads.

   The work is identical; it just stops when there is nothing to
   do. Three guards, applied consistently:
     · document.hidden  → freeze, resume on visibilitychange
     · scroll unchanged → skip the frame entirely
     · far off-screen   → skip that element
   ============================================================ */
(function () {
  "use strict";
  var root = document.querySelector(".cine");
  if (!root) return;

  var reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var isTouch = window.matchMedia("(hover: none), (pointer: coarse)").matches;
  var raf = window.requestAnimationFrame.bind(window);
  var lerp = function (a, b, t) { return a + (b - a) * t; };
  var clamp = function (v, a, b) { return Math.max(a, Math.min(b, v)); };

  /* ---------------------------------------------------------
     1 · SMOOTH SCROLL (lightweight Lenis-style)
     Translates a wrapper on transform for buttery momentum.
     Falls back to native scroll on touch / reduced-motion.
  --------------------------------------------------------- */
  var scroller = document.getElementById("cineScroll");
  var smooth = { target: 0, current: 0, active: false };

  function setupSmooth() {
    if (reduce || isTouch || !scroller) return; // native scroll everywhere it matters
    // The cinematic scroll-hero (wb-hero-cinema, [data-wh]) pins with GSAP ScrollTrigger,
    // which is incompatible with this transform-based smooth scroll (it fixes #cineScroll
    // and overrides body height). When the hero is present, use native scroll everywhere.
    if (document.querySelector("[data-wh]")) return;
    document.documentElement.classList.add("cine-lenis");
    document.body.style.height = scroller.getBoundingClientRect().height + "px";
    scroller.style.position = "fixed";
    scroller.style.top = "0";
    scroller.style.left = "0";
    scroller.style.width = "100%";
    scroller.style.willChange = "transform";
    smooth.active = true;

    function resize() {
      document.body.style.height = scroller.getBoundingClientRect().height + "px";
    }
    window.addEventListener("resize", resize);
    if ("ResizeObserver" in window) new ResizeObserver(resize).observe(scroller);

    function loop() {
      if (document.hidden) { raf(loop); return; }          // nothing to ease while hidden
      smooth.target = window.scrollY || window.pageYOffset;
      smooth.current = lerp(smooth.current, smooth.target, 0.09);
      if (Math.abs(smooth.target - smooth.current) < 0.06) smooth.current = smooth.target;
      scroller.style.transform = "translate3d(0," + (-smooth.current) + "px,0)";
      raf(loop);
    }
    loop();
  }

  // unified "scrollY" accessor (smooth uses eased value for parallax)
  function sy() { return smooth.active ? smooth.current : (window.scrollY || window.pageYOffset); }

  /* ---------------------------------------------------------
     2 · REVEAL on enter (IntersectionObserver)
  --------------------------------------------------------- */
  function setupReveal() {
    var els = root.querySelectorAll(".rise, [data-reveal]");
    if (!("IntersectionObserver" in window) || reduce) {
      els.forEach(function (e) { e.classList.add("is-in"); });
      return;
    }
    var io = new IntersectionObserver(function (ents) {
      ents.forEach(function (en) {
        if (en.isIntersecting) {
          var el = en.target;
          var d = el.getAttribute("data-delay");
          if (d) el.style.transitionDelay = d + "ms";
          el.classList.add("is-in");
          io.unobserve(el);
        }
      });
    }, { rootMargin: "0px 0px -12% 0px", threshold: 0.12 });
    els.forEach(function (e) { io.observe(e); });

    // section-level "is-in" toggles (for tech labels, hidden outline)
    var secs = root.querySelectorAll("[data-insec]");
    var io2 = new IntersectionObserver(function (ents) {
      ents.forEach(function (en) { en.target.classList.toggle("is-in", en.isIntersecting); });
    }, { threshold: 0.25 });
    secs.forEach(function (s) { io2.observe(s); });
  }

  /* ---------------------------------------------------------
     3 · NAV: sticky blur + scroll progress + mobile drawer
  --------------------------------------------------------- */
  function setupNav() {
    var nav = document.getElementById("cineNav");
    var prog = document.getElementById("cineProgress");
    function onScroll() {
      var y = window.scrollY || window.pageYOffset;
      if (nav) nav.classList.toggle("is-stuck", y > 30);
      if (prog) {
        var h = document.documentElement.scrollHeight - window.innerHeight;
        prog.style.width = (h > 0 ? (y / h) * 100 : 0) + "%";
      }
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    // smooth anchor scrolling
    root.querySelectorAll('a[data-scroll]').forEach(function (a) {
      a.addEventListener("click", function (e) {
        var id = a.getAttribute("href");
        if (id && id[0] === "#") {
          var t = document.querySelector(id);
          if (t) {
            e.preventDefault();
            var top = t.getBoundingClientRect().top + (window.scrollY || 0) - 10;
            window.scrollTo({ top: top, behavior: reduce ? "auto" : "smooth" });
            closeDrawer();
          }
        }
      });
    });

    // mobile drawer
    var burger = document.getElementById("cineBurger");
    var drawer = document.getElementById("cineDrawer");
    function closeDrawer() { if (drawer) drawer.classList.remove("open"); }
    window._cineCloseDrawer = closeDrawer;
    if (burger && drawer) {
      burger.addEventListener("click", function () { drawer.classList.add("open"); });
      drawer.querySelectorAll("[data-close]").forEach(function (b) {
        b.addEventListener("click", closeDrawer);
      });
    }
  }
  function closeDrawer(){ if(window._cineCloseDrawer) window._cineCloseDrawer(); }

  /* ---------------------------------------------------------
     4 · HERO 3D tilt (mouse) + subtle parallax layers
  --------------------------------------------------------- */
  function setupHero() {
    var stage = document.getElementById("heroStage");
    if (!stage || reduce) return;
    var inner = stage.querySelector(".stage-inner");
    if (!inner) return;
    var tx = 0, ty = 0, cx = 0, cy = 0, running = false;

    function onMove(e) {
      var r = stage.getBoundingClientRect();
      var px = (e.clientX - r.left) / r.width - 0.5;
      var py = (e.clientY - r.top) / r.height - 0.5;
      tx = clamp(px, -0.5, 0.5); ty = clamp(py, -0.5, 0.5);
      if (!running) { running = true; raf(tick); }
    }
    function onLeave() { tx = 0; ty = 0; if (!running) { running = true; raf(tick); } }
    function tick() {
      cx = lerp(cx, tx, 0.08); cy = lerp(cy, ty, 0.08);
      inner.style.transform = "rotateY(" + (cx * 16) + "deg) rotateX(" + (-cy * 12) + "deg)";
      if (Math.abs(cx - tx) > 0.001 || Math.abs(cy - ty) > 0.001) raf(tick);
      else running = false;
    }
    // hero reacts to pointer over the whole first viewport
    var host = stage.closest(".cine-hero") || stage;
    host.addEventListener("mousemove", onMove);
    host.addEventListener("mouseleave", onLeave);
  }

  /* ---------------------------------------------------------
     5 · Generic scroll-parallax (data-para = strength)
     Elements more than one viewport away are skipped — their
     transform cannot be seen, so computing it is pure waste.
  --------------------------------------------------------- */
  var paraEls = [];
  function collectPara() {
    paraEls = [].slice.call(root.querySelectorAll("[data-para]")).map(function (el) {
      return { el: el, k: parseFloat(el.getAttribute("data-para")) || 0.1 };
    });
  }
  function runPara() {
    var vh = window.innerHeight;
    for (var i = 0; i < paraEls.length; i++) {
      var p = paraEls[i];
      var r = p.el.getBoundingClientRect();
      if (r.bottom < -vh || r.top > vh * 2) continue;      // comfortably off-screen
      var center = r.top + r.height / 2 - vh / 2;
      p.el.style.transform = "translate3d(0," + (-center * p.k) + "px,0)";
    }
  }

  /* ---------------------------------------------------------
     6 · EXPLODED TECH — scroll scrubs layer separation
     Uses the section's progress through its sticky runway.
  --------------------------------------------------------- */
  var techEls = null;
  function setupTech() {
    var sec = document.getElementById("cineTech");
    if (!sec) return;
    techEls = {
      sec: sec,
      layers: [].slice.call(sec.querySelectorAll("[data-layer]"))
    };
  }
  function runTech() {
    if (!techEls) return;
    var sec = techEls.sec;
    var r = sec.getBoundingClientRect();
    var vh = window.innerHeight;
    if (r.bottom < -vh || r.top > vh * 2) return;          // runway nowhere near the viewport
    var total = sec.offsetHeight - vh;
    var p = clamp((-r.top) / (total || 1), 0, 1);
    techEls.layers.forEach(function (l) {
      var dir = parseFloat(l.getAttribute("data-layer")) || 0; // -2..+2 spread
      var spread = dir * 130 * p;                  // px vertical fan-out
      var rot = dir * 4 * p;
      l.style.transform = "translate3d(0," + spread + "px,0) rotateX(" + rot + "deg)";
      l.style.opacity = String(0.55 + 0.45 * p);
    });
  }

  /* ---------------------------------------------------------
     7 · LIGHTBOX (showcase cards)
  --------------------------------------------------------- */
  function setupLightbox() {
    var lb = document.getElementById("cineLb");
    if (!lb) return;
    var img = lb.querySelector("img");
    var cap = lb.querySelector("figcaption");
    root.querySelectorAll("[data-lb-src]").forEach(function (c) {
      c.addEventListener("click", function () {
        img.src = c.getAttribute("data-lb-src");
        cap.textContent = c.getAttribute("data-lb-cap") || "";
        lb.classList.add("open");
        document.body.style.overflow = "hidden";
      });
    });
    function close() { lb.classList.remove("open"); document.body.style.overflow = ""; }
    lb.addEventListener("click", function (e) { if (e.target === lb || e.target.closest(".x")) close(); });
    document.addEventListener("keydown", function (e) { if (e.key === "Escape") close(); });
  }

  /* ---------------------------------------------------------
     8 · COLD → WARM interactive slider + particle color shift
  --------------------------------------------------------- */
  function setupTemp() {
    var wrap = document.getElementById("cineTemp");
    if (!wrap) return;
    var sec = wrap.closest(".cine-temp");
    var canvas = wrap.querySelector("canvas.tparticles");
    var ctx = canvas ? canvas.getContext("2d") : null;
    var val = 0.5; // 0 cold → 1 warm
    var dragging = false;

    function apply(v) {
      val = clamp(v, 0, 1);
      var pct = (val * 100).toFixed(1) + "%";
      wrap.style.setProperty("--clip", pct);
      if (sec) sec.style.setProperty("--t", val.toFixed(3));
    }
    function fromEvent(clientX) {
      var r = wrap.getBoundingClientRect();
      apply((clientX - r.left) / r.width);
    }
    wrap.addEventListener("pointerdown", function (e) { dragging = true; wrap.setPointerCapture(e.pointerId); fromEvent(e.clientX); });
    wrap.addEventListener("pointermove", function (e) { if (dragging) fromEvent(e.clientX); });
    wrap.addEventListener("pointerup", function () { dragging = false; });
    wrap.addEventListener("pointercancel", function () { dragging = false; });
    apply(0.5);

    // particles: blue on cold side, orange on warm side
    if (ctx && !reduce) {
      var parts = [], N = isTouch ? 26 : 46, W = 0, H = 0;
      var inView = false, looping = false;
      function size() { W = canvas.width = wrap.clientWidth; H = canvas.height = wrap.clientHeight; }
      function seed() {
        parts = [];
        for (var i = 0; i < N; i++) parts.push({
          x: Math.random(), y: Math.random(),
          vx: (Math.random() - 0.5) * 0.0006, vy: -(0.0004 + Math.random() * 0.0009),
          r: 1 + Math.random() * 2.4, o: 0.3 + Math.random() * 0.5
        });
      }
      function draw() {
        if (!inView || document.hidden) { looping = false; return; }   // stop, don't spin
        ctx.clearRect(0, 0, W, H);
        for (var i = 0; i < parts.length; i++) {
          var p = parts[i];
          p.x += p.vx; p.y += p.vy;
          if (p.y < -0.02) { p.y = 1.02; p.x = Math.random(); }
          if (p.x < 0) p.x = 1; if (p.x > 1) p.x = 0;
          var warm = p.x < val;                       // left of divider = warm? clip warms from left
          var col = warm ? "255,150,90" : "150,195,235";
          ctx.beginPath();
          ctx.arc(p.x * W, p.y * H, p.r, 0, 6.283);
          ctx.fillStyle = "rgba(" + col + "," + p.o + ")";
          ctx.fill();
        }
        raf(draw);
      }
      function start() { if (!looping && inView && !document.hidden) { looping = true; raf(draw); } }
      size(); seed();
      window.addEventListener("resize", function () { size(); }, { passive: true });
      document.addEventListener("visibilitychange", start);
      var io = new IntersectionObserver(function (e) {
        inView = e[0].isIntersecting;
        if (inView) { size(); start(); }
      }, { threshold: 0.05 });
      io.observe(wrap);
    }
  }

  /* ---------------------------------------------------------
     9 · PARALLAX VIEWER — drag to rotate/tilt (fake 3D)
  --------------------------------------------------------- */
  function setupViewer() {
    var stage = document.getElementById("cineViewer");
    if (!stage) return;
    var prod = stage.querySelector(".vprod");
    if (!prod) return;
    var rx = 0, ry = 0, trx = 0, try_ = 0, sc = 1, tsc = 1, running = false;
    var dragging = false, lastX = 0, lastY = 0;

    function tick() {
      rx = lerp(rx, trx, 0.12); ry = lerp(ry, try_, 0.12); sc = lerp(sc, tsc, 0.12);
      prod.style.transform = "rotateY(" + ry + "deg) rotateX(" + rx + "deg) scale(" + sc + ")";
      if (Math.abs(rx - trx) > 0.01 || Math.abs(ry - try_) > 0.01 || Math.abs(sc - tsc) > 0.001) raf(tick);
      else running = false;
    }
    function kick() { if (!running) { running = true; raf(tick); } }
    function down(x, y) { dragging = true; lastX = x; lastY = y; }
    function move(x, y) {
      if (!dragging) return;
      try_ = clamp(try_ + (x - lastX) * 0.4, -50, 50);
      trx = clamp(trx - (y - lastY) * 0.3, -30, 30);
      lastX = x; lastY = y; kick();
    }
    function up() { dragging = false; if (!isTouch) { trx = 0; try_ = 0; kick(); } }

    stage.addEventListener("mousedown", function (e) { down(e.clientX, e.clientY); });
    window.addEventListener("mousemove", function (e) { move(e.clientX, e.clientY); });
    window.addEventListener("mouseup", up);
    stage.addEventListener("touchstart", function (e) { down(e.touches[0].clientX, e.touches[0].clientY); }, { passive: true });
    stage.addEventListener("touchmove", function (e) { move(e.touches[0].clientX, e.touches[0].clientY); }, { passive: true });
    stage.addEventListener("touchend", up);

    // Subtle idle auto-rotate when not interacting (desktop only).
    // Gated on visibility + on-screen: this used to tick every 60ms for the
    // life of the page even with the viewer far out of view.
    if (!isTouch && !reduce) {
      var idle = 0, inView = false, timer = null;
      function spin() {
        if (document.hidden || !inView || dragging) return;
        idle += 1; try_ = Math.sin(idle / 22) * 14; kick();
      }
      function startTimer() { if (!timer) timer = setInterval(spin, 60); }
      function stopTimer() { if (timer) { clearInterval(timer); timer = null; } }
      if ("IntersectionObserver" in window) {
        new IntersectionObserver(function (e) {
          inView = e[0].isIntersecting;
          if (inView && !document.hidden) startTimer(); else stopTimer();
        }, { threshold: 0.05 }).observe(stage);
      } else {
        inView = true; startTimer();
      }
      document.addEventListener("visibilitychange", function () {
        if (document.hidden) stopTimer(); else if (inView) startTimer();
      });
    }

    // buttons
    if (stage.parentElement) {
      stage.parentElement.querySelectorAll("[data-view]").forEach(function (b) {
        b.addEventListener("click", function () {
          var a = b.getAttribute("data-view");
          if (a === "in") tsc = clamp(tsc + 0.15, 1, 1.8);
          if (a === "out") tsc = clamp(tsc - 0.15, 0.8, 1.8);
          if (a === "reset") { tsc = 1; trx = 0; try_ = 0; }
          kick();
        });
      });
    }
  }

  /* ---------------------------------------------------------
    10 · BACKGROUND PARTICLES (fixed, ambient, cheap)
  --------------------------------------------------------- */
  function setupParticles() {
    var canvas = document.getElementById("cineParticles");
    if (!canvas || reduce) return;
    var ctx = canvas.getContext("2d");
    var W, H, parts, N = isTouch ? 22 : 42, looping = false;
    function size() { W = canvas.width = window.innerWidth; H = canvas.height = window.innerHeight; }
    function seed() {
      parts = [];
      for (var i = 0; i < N; i++) parts.push({
        x: Math.random() * 1, y: Math.random() * 1,
        vy: -(0.00006 + Math.random() * 0.00016),
        vx: (Math.random() - 0.5) * 0.00006,
        r: 0.8 + Math.random() * 2.2, o: 0.12 + Math.random() * 0.3
      });
    }
    function draw() {
      if (document.hidden) { looping = false; return; }    // freeze in a background tab
      ctx.clearRect(0, 0, W, H);
      for (var i = 0; i < parts.length; i++) {
        var p = parts[i]; p.y += p.vy; p.x += p.vx;
        if (p.y < -0.02) { p.y = 1.02; p.x = Math.random(); }
        ctx.beginPath();
        ctx.arc(p.x * W, p.y * H, p.r, 0, 6.283);
        ctx.fillStyle = "rgba(206,139,119," + p.o + ")";
        ctx.fill();
      }
      raf(draw);
    }
    function start() { if (!looping && !document.hidden) { looping = true; raf(draw); } }
    size(); seed(); start();
    window.addEventListener("resize", function () { size(); seed(); }, { passive: true });
    document.addEventListener("visibilitychange", start);
  }

  /* ---------------------------------------------------------
     master rAF for scroll-driven effects (single loop)
     Only does work when the page is visible AND has actually
     moved since the last frame. Standing still on a section
     now costs nothing instead of ~60 layout reads a second.
  --------------------------------------------------------- */
  var lastY = null, dirty = true;
  function markDirty() { dirty = true; }

  function masterLoop() {
    if (!document.hidden) {
      var y = sy();
      if (dirty || y !== lastY) {
        lastY = y;
        dirty = false;
        runPara();
        runTech();
      }
    }
    raf(masterLoop);
  }

  /* ---------- init ---------- */
  function init() {
    setupSmooth();
    setupReveal();
    setupNav();
    setupHero();
    collectPara();
    setupTech();
    setupLightbox();
    setupTemp();
    setupViewer();
    setupParticles();
    if (!reduce) raf(masterLoop);
    window.addEventListener("resize", function () { collectPara(); markDirty(); }, { passive: true });
    window.addEventListener("orientationchange", markDirty, { passive: true });
    document.addEventListener("visibilitychange", markDirty);
    // Lazily-loaded imagery changes layout after paint — recompute once it settles.
    window.addEventListener("load", markDirty, { once: true });
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
})();
